/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
/**
 * Documentation content for the in-app Help Center.
 *
 * Pure data so the article copy is easy to edit without touching render logic.
 * Each article is a list of typed blocks; see HELP screen (index.jsx) for the
 * renderer that maps these to design-system components.
 *
 * Block shapes:
 *   { t: 'h', text }                         — section heading
 *   { t: 'p', text }                         — paragraph
 *   { t: 'bullets', items: [text, ...] }     — bulleted list
 *   { t: 'steps', items: [[title, body]] }   — numbered steps
 *   { t: 'callout', tone, title, text }      — tip | info | warn
 *   { t: 'table', head: [...], rows: [[...]] }
 *   { t: 'cards', items: [[icon, title, text]] }
 *   { t: 'activities' }                      — the five activity-type legend
 */

const ARTICLES = [
  // ─────────────────────────────────────────────── Getting started
  {
    id: 'getting-started',
    label: 'Getting started',
    icon: 'Compass',
    title: 'Getting started',
    lead: 'As an admin or team leader you spend most of your time across three connected areas — Users, Teams and Reporting. This guide takes them in the order you would naturally set them up.',
    where: null,
    blocks: [
      { t: 'h', text: 'The three areas' },
      {
        t: 'cards',
        items: [
          ['Users', 'Users', 'Invite people, set account types and reporting managers, and control roles & permissions.'],
          ['Layers', 'Teams', 'Group members under leaders, track engagement, and tune per-team policies & visibility.'],
          ['BarChart3', 'Reporting', 'Read time worked, activity quality and audit results, then act on what the data shows.'],
        ],
      },
      { t: 'h', text: 'Who can do what' },
      { t: 'p', text: 'Every screen is gated by an account type. Higher levels inherit everything below them — this decides which items a person even sees in the left sidebar.' },
      {
        t: 'table',
        head: ['Account type', 'Level', 'Typical access'],
        rows: [
          ['Primary owner', '5', 'Everything, plus billing & workspace ownership'],
          ['Workspace owner', '4', 'Manage the workspace, all members & settings'],
          ['Workspace admin', '3', 'Members, teams, roles, reports & settings'],
          ['Team Leader', '2', 'Own team engagement, PTO & timesheets'],
          ['Team member', '1', 'Own activity, timesheets & PTO requests'],
        ],
      },
      { t: 'callout', tone: 'info', title: 'Roles go further than account types', text: 'Custom roles let you bundle exact permissions on top of the built-in tiers. See the Roles & permissions article.' },
      { t: 'h', text: 'A simple admin rhythm' },
      { t: 'p', text: 'When setting up a workspace — or onboarding a new team — this order keeps things tidy. The first three are one-time setup; the last two are ongoing.' },
      {
        t: 'steps',
        items: [
          ['Add users', 'Invite or import people; set account types and reporting managers.'],
          ['Set roles', 'Assign roles so each person sees only what they should.'],
          ['Build teams', 'Group members under leaders and tune team settings.'],
          ['Read reports', 'Watch Time Worked and engagement on a regular cadence.'],
          ['Act on flags', 'Review audits and alerts; follow up where it matters.'],
        ],
      },
      { t: 'h', text: 'Finding your way' },
      { t: 'p', text: 'The left sidebar adapts to your role. The key destinations referenced in this guide:' },
      {
        t: 'bullets',
        items: [
          'Team Engagement — teams and their engagement scores.',
          'Reports — Time Worked, audit results and timesheets.',
          'Manage Members — the full workspace user list.',
          'Settings & Permissions — screenshots, webcam and Team Management.',
          'Role Management — custom roles and permissions (owners).',
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────── Managing users
  {
    id: 'users',
    label: 'Managing users',
    icon: 'Users',
    title: 'Managing users',
    lead: 'The Workspace Members screen is where every person in your workspace lives. From here you invite new people, edit their details, and manage their access and tracking.',
    where: 'Sidebar → Manage Members',
    blocks: [
      { t: 'h', text: 'The Members table' },
      { t: 'p', text: 'Each row is one member. Use the controls above the table to focus the list:' },
      {
        t: 'bullets',
        items: [
          'Status filter — switch between Active, Inactive and All members.',
          'Search — find a member by name.',
          'Sort — click any column header to sort by it.',
        ],
      },
      { t: 'p', text: 'A member’s state is shown as a coloured status pill:' },
      {
        t: 'table',
        head: ['Status', 'Meaning'],
        rows: [
          ['Active', 'Member has accepted the invite and is tracking.'],
          ['Invited', 'Invitation sent — awaiting acceptance.'],
          ['Deactivated', 'Access revoked; can be reactivated later.'],
        ],
      },
      { t: 'h', text: 'Adding a member' },
      { t: 'p', text: 'Click + New Member and complete the form. An email invitation is sent automatically.' },
      {
        t: 'steps',
        items: [
          ['Email address', 'Where the invitation is sent.'],
          ['Account type', 'Team member, Team Leader, Workspace owner or Workspace admin.'],
          ['Department & designation', 'Pick from your workspace lists.'],
          ['Reporting manager', 'Sets the organisation-chart relationship.'],
          ['Projects (optional)', 'Assign the member to projects on creation.'],
        ],
      },
      { t: 'h', text: 'Bulk importing members' },
      { t: 'p', text: 'To add many people at once, click Import Users and upload a CSV.' },
      {
        t: 'steps',
        items: [
          ['Prepare your sheet', 'One row per person, with email and account-type columns.'],
          ['Upload the file', 'The importer previews your rows before anything is saved.'],
          ['Match the columns', 'Map each spreadsheet column to a Shoot field.'],
          ['Send invitations', 'Everyone is created together and emailed an invite.'],
        ],
      },
      { t: 'h', text: 'Managing an existing user' },
      { t: 'p', text: 'Open the row’s “···” (more) menu to reach every per-user action without leaving the table:' },
      {
        t: 'bullets',
        items: [
          'Edit User — update details, reporting manager and department.',
          'Resend Invitation — for members still in the Invited state.',
          'Setup Alerts — configure activity / violation alerts.',
          'Idle Time Tracking — enable or disable per member.',
          'Manual Tracker — allow manual time entries.',
          'Deactivate User — revoke access (reversible).',
        ],
      },
      { t: 'callout', tone: 'tip', title: 'Tip', text: 'Account type sets the broad tier. Use Role Management to fine-tune exactly which screens and actions a role gets — see Roles & permissions.' },
    ],
  },

  // ─────────────────────────────────────────────── Roles & permissions
  {
    id: 'roles',
    label: 'Roles & permissions',
    icon: 'KeyRound',
    title: 'Roles & permissions',
    lead: 'Roles let you go beyond the five built-in account types. Build custom roles that bundle exactly the permissions a job needs, then assign them to people.',
    where: 'Sidebar → Role Management',
    blocks: [
      { t: 'h', text: 'Creating and assigning a role' },
      {
        t: 'steps',
        items: [
          ['Create a role', 'Click Create Role, then give it a name, description and Level (1–5). A higher level means broader reach.'],
          ['Pick permissions', 'Toggle permission chips, grouped by resource — dashboard, report, team, member, settings and more.'],
          ['Assign to users', 'Use Assign Roles to map members to the roles you have built.'],
        ],
      },
      { t: 'h', text: 'System vs custom roles' },
      { t: 'p', text: 'Every role is marked System or Custom:' },
      {
        t: 'bullets',
        items: [
          'System roles map to the built-in account types and are view-only — you can inspect their permissions but not change them.',
          'Custom roles are fully editable: rename them, re-scope their permissions, or delete them.',
        ],
      },
      { t: 'callout', tone: 'info', title: 'Permissions are grouped by resource', text: 'Examples include dashboard:read, report:read, team:read, member:manage, settings:update, audit:webcam, pto:read:team and activity:read:all.' },
      { t: 'callout', tone: 'warn', title: 'Changing your own role', text: 'If you edit a role you currently hold, your access refreshes immediately so the change takes effect for you.' },
    ],
  },

  // ─────────────────────────────────────────────── Managing teams
  {
    id: 'teams',
    label: 'Managing teams',
    icon: 'Layers',
    title: 'Managing teams',
    lead: 'Teams group members under one or more leaders. The Team Engagement hub is your home base — KPIs at the top, quick filters, and a card for every team.',
    where: 'Sidebar → Team Engagement',
    blocks: [
      { t: 'h', text: 'The Team Engagement hub' },
      { t: 'p', text: 'The summary KPIs give you the shape of the workspace at a glance:' },
      {
        t: 'bullets',
        items: [
          'Total Teams — and how many members are tracked.',
          'Avg Engagement — the average score across all teams.',
          'Teams At Risk — teams below 40% engagement.',
        ],
      },
      { t: 'p', text: 'Use the engagement filters (All / High / Medium / At Risk) and the search box to narrow the cards below.' },
      { t: 'h', text: 'Creating a team' },
      { t: 'p', text: 'Workspace admins see a Create Team button. A team needs a name, at least one leader, and members.' },
      {
        t: 'steps',
        items: [
          ['Team name', 'A clear, recognisable name.'],
          ['Add leaders', 'One or more members who will lead the team.'],
          ['Add team members', 'The tracked contributors on the team.'],
        ],
      },
      { t: 'p', text: 'After creating, you can edit a team from its card at any time, drill in to see per-member activity, or delete it — deleting a team leaves its members in the workspace.' },
      { t: 'h', text: 'Roles inside a team' },
      {
        t: 'cards',
        items: [
          ['UserCog', 'Leaders', 'See their team’s engagement, timesheets and PTO. Usually a Team Leader account type.'],
          ['Users', 'Members', 'Tracked contributors whose activity rolls up into the team score.'],
        ],
      },
      { t: 'h', text: 'Team Management settings' },
      { t: 'p', text: 'Workspace-wide team policies live under Settings & Permissions → Team Management, organised into four tabs:' },
      {
        t: 'cards',
        items: [
          ['Table', 'Team Management', 'Audit and filter every member’s team assignments, screenshot rate, webcam audits and PTO policy — and fix anyone left Unassigned.'],
          ['Sliders', 'Global Team Settings', 'Set organisation-wide defaults: time-off policies, webcam-audit frequency, screenshot cadence and activity tracking.'],
          ['Eye', 'Team Visibility Controls', 'Decide who can view which teams and individual members.'],
          ['Mail', 'Productivity Email Settings', 'Manage the workspace’s automated productivity emails.'],
        ],
      },
      { t: 'h', text: 'How the engagement score works' },
      { t: 'callout', tone: 'info', title: 'The calculation', text: 'Engagement = Active time ÷ Total tracked time — computed per member over the chosen date range, then averaged across the team.' },
      { t: 'p', text: 'Use the bands to decide where to focus:' },
      {
        t: 'table',
        head: ['Band', 'Range', 'What to do'],
        rows: [
          ['High', '70% +', 'Healthy — keep doing what works.'],
          ['Medium', '40–69%', 'On track, but worth a closer look.'],
          ['At Risk', 'Below 40%', 'Flagged on the dashboard — act here first.'],
        ],
      },
      { t: 'callout', tone: 'warn', title: 'Violations', text: 'Team cards also flag webcam or geo-location violations independently of the engagement score.' },
    ],
  },

  // ─────────────────────────────────────────────── Reporting
  {
    id: 'reporting',
    label: 'Reporting',
    icon: 'BarChart3',
    title: 'Reporting',
    lead: 'Reports turn tracked activity into decisions. The Reports group in the sidebar collects every read-only view; availability depends on your permissions.',
    where: 'Sidebar → Reports',
    blocks: [
      { t: 'h', text: 'The reports at a glance' },
      {
        t: 'cards',
        items: [
          ['Clock', 'Time Worked', 'Hours, quality time and engagement per day, person or team.'],
          ['Camera', 'Webcam Audit Results', 'Review captured webcam-audit checks and their outcomes.'],
          ['ShieldAlert', 'Insider Threats', 'Surface risky behaviour patterns flagged for review.'],
          ['Table', 'Timesheets', 'Approve and review submitted time across the team.'],
          ['MapPin', 'Geolocation Results', 'Location-audit results and any geo-fence violations.'],
          ['CalendarOff', 'Team Members’ Time Off', 'Track PTO requests and balances for your reports.'],
        ],
      },
      { t: 'h', text: 'Inside the Time Worked report' },
      { t: 'p', text: 'Pick a date range, optionally filter to a team or member, then read four KPIs, a per-day breakdown chart, and an exportable table.' },
      {
        t: 'bullets',
        items: [
          'Date range — defaults to the last 7 days; pick any window.',
          'Filter — narrow to one team or a single member.',
          'KPIs — Total Hours, Manual Hours, Quality Hours and Engagement Score.',
          'Daily Activity — expand any day to see time by activity type.',
          'Export Table — download the breakdown as a CSV file.',
        ],
      },
      { t: 'h', text: 'The five activity types' },
      { t: 'p', text: 'Every report colours tracked time by these categories. Only the first three count toward the engagement (quality) score.' },
      { t: 'activities' },
      { t: 'h', text: 'Audit & compliance' },
      { t: 'p', text: 'Three reports help you verify presence and catch anomalies:' },
      {
        t: 'bullets',
        items: [
          'Webcam Audit Results — periodic checks confirm the right person is present; set the frequency in Settings → Webcam Audit.',
          'Geolocation Results — confirms members work from allowed locations; breaches appear as location violations.',
          'Insider Threats — highlights unusual or risky behaviour; Analytics → Unusual Activity goes deeper.',
        ],
      },
      { t: 'callout', tone: 'tip', title: 'Don’t just read — get alerted', text: 'Use Setup Alerts on a member (see Managing users) so webcam, location or activity issues notify you the moment they happen.' },
    ],
  },

  // ─────────────────────────────────────────────── How activity is calculated
  {
    id: 'activity',
    label: 'How activity is calculated',
    icon: 'Activity',
    title: 'How activity is calculated',
    lead: 'Every percentage you see in Shoot — engagement score, quality hours, the colour of a snapshot — traces back to one simple measurement taken on the desktop app. This article follows that number from a single second of typing all the way to the Time Worked report.',
    where: 'Reports → Time Worked',
    blocks: [
      { t: 'h', text: 'The building block: a 10-minute slot' },
      { t: 'p', text: 'The desktop app divides the working day into 10-minute slots aligned to the clock — :00, :10, :20 and so on. Each slot is measured independently and uploaded on its own, which is why activity can change from one row to the next.' },
      { t: 'p', text: 'Inside a slot, the app watches the keyboard and mouse second by second. Any second in which you pressed a key, clicked, moved the mouse or scrolled is banked as one active second. It makes no difference whether that second held one keystroke or fifty — a second is either active or it is not.' },
      { t: 'callout', tone: 'info', title: 'The formula', text: 'Activity % = active seconds ÷ tracked seconds in the slot × 100. In a full 10-minute slot that means 600 tracked seconds; three minutes of steady typing and clicking gives roughly 30%.' },
      { t: 'h', text: 'What does and does not count' },
      {
        t: 'bullets',
        items: [
          'Counts — key presses, mouse clicks, mouse movement and scroll wheel, anywhere on the machine, not just inside Shoot.',
          'Counts once — keyboard and mouse are combined, so typing with one hand and moving the mouse with the other does not double your score.',
          'Counts — work done inside a remote-desktop session, so people who work on a remote machine are not penalised.',
          'Removed — time while the screen is locked. Locked seconds come out of the total, so stepping away does not dilute the slot you were working in.',
          'Never counted — what you typed. Only the timing of input is measured; keystroke content is never recorded or transmitted.',
        ],
      },
      { t: 'h', text: 'Idle slots' },
      { t: 'p', text: 'A slot that ends with five or fewer active seconds is treated as idle rather than as very low activity. When you come back to the machine, the app asks what to do with that stretch of time.' },
      {
        t: 'bullets',
        items: [
          'Keep the time — the slot is uploaded as tracked time at 0% activity. It counts toward total hours but not toward quality hours.',
          'Discard the time — the slot is dropped entirely and appears nowhere in reports.',
        ],
      },
      { t: 'callout', tone: 'tip', title: 'Idle prompts can be turned off per person', text: 'Idle Time Tracking is a per-member switch in the Manage Members “···” menu. See Managing users.' },
      { t: 'h', text: 'Meetings earn partial credit' },
      { t: 'p', text: 'A video call is real work that produces almost no keyboard or mouse input, so measuring it the normal way would score an hour of meetings close to zero. Shoot compensates by giving meeting time its own credit.' },
      { t: 'p', text: 'While a recognised meeting is the app you are actually looking at, every second is banked as meeting time:' },
      {
        t: 'table',
        head: ['What you are using', 'Recognised as a meeting'],
        rows: [
          ['Zoom desktop app', 'Yes — while the Zoom meeting window is in front'],
          ['Zoom in a browser', 'Yes — on the app.zoom.us tab'],
          ['Google Meet in a browser', 'Yes — on the meet.google.com tab'],
        ],
      },
      { t: 'callout', tone: 'info', title: 'The meeting formula', text: 'Every second in a meeting is worth half a second of activity credit, and that credit can only fill seconds you were not already active for. Meeting credit can never push a slot above 100%.' },
      { t: 'p', text: 'A worked example. In a full 10-minute slot you spend the whole time on a Zoom call and type for two minutes of it. Your input alone gives 120 active seconds, or 20%. The call adds 300 seconds of credit, but only 480 seconds were uncredited, so the full 300 is applied: 420 active seconds, or 70%. Sitting silently through the same 10-minute call with no input at all gives exactly 50%.' },
      {
        t: 'bullets',
        items: [
          'The credit is folded into the ordinary activity percentage — there is no separate meeting column in reports.',
          'Hovering a snapshot shows the meeting contribution on its own, alongside the mouse and keyboard figures.',
          'Meeting time also suppresses the low-keyboard and low-mouse unusual-activity checks, so a quiet call is not flagged as suspicious.',
        ],
      },
      { t: 'callout', tone: 'warn', title: 'Only Zoom and Google Meet are recognised', text: 'Microsoft Teams, Webex, Slack huddles and Shoot’s own video calls do not currently earn meeting credit. Browser-tab detection also works on macOS only — on Windows, use the Zoom desktop app to be credited.' },
      { t: 'h', text: 'From slots to the numbers in reports' },
      { t: 'p', text: 'Slots are added up per day and per member. Everything on the Time Worked report and the dashboard comes from those two running totals.' },
      {
        t: 'table',
        head: ['Figure', 'How it is derived'],
        rows: [
          ['Total Hours', 'All tracked seconds in the range, idle slots included.'],
          ['Quality Hours', 'All active seconds in the range — the productive portion of Total Hours.'],
          ['Engagement Score', 'Quality Hours ÷ Total Hours, as a rounded percentage.'],
          ['Idle Time', 'Total Hours minus Quality Hours.'],
          ['Manual Hours', 'Time entered by hand. Reported separately and never mixed into the three figures above.'],
        ],
      },
      { t: 'callout', tone: 'warn', title: 'Manual hours do not move the score', text: 'Manual time is added by a leader to cover work the tracker missed. It appears in its own KPI and its own row, but it is deliberately excluded from Quality Hours and the engagement score, because no activity was measured for it.' },
      { t: 'h', text: 'How a day is classified' },
      { t: 'p', text: 'The per-day breakdown sorts each day into one of the activity types below, using that day’s overall activity percentage.' },
      {
        t: 'table',
        head: ['Day’s activity', 'Classified as'],
        rows: [
          ['Above 51%', 'Deep Focused'],
          ['21% – 51%', 'Focused'],
          ['20% and below', 'Active'],
        ],
      },
      { t: 'activities' },
      { t: 'callout', tone: 'tip', title: 'Reading a low score fairly', text: 'Design, review, reading and calls all produce genuine work with little input. Before acting on a low engagement score, open the member’s activity day and look at which applications the time was spent in.' },
    ],
  },

  // ─────────────────────────────────────────────── Bot alerts
  {
    id: 'bot-alerts',
    label: 'Bot alerts',
    icon: 'Bot',
    title: 'Bot alerts',
    lead: 'Bot alerts are Shoot’s automated watchdogs. Three bots — HR, IT and Infosec — check tracked activity against your working-hours policy and raise an alert when something falls outside it, then walk the employee through acknowledging it.',
    where: 'Shoot Insights — /members/alerts',
    blocks: [
      { t: 'h', text: 'Finding the alerts screen' },
      { t: 'p', text: 'Bot alerts live on the Shoot Insights screen. It has no sidebar entry of its own — reach it from the Critical Alerts card on your dashboard, or from View Alerts in the organisation chart. The screen is split into five tabs:' },
      {
        t: 'bullets',
        items: [
          'Critical Alerts — anything escalated to critical priority, across all three bots.',
          'Team Performance — hours, idle time and a High / Average / Low band for every team.',
          'HR Bot Alerts — schedule and attendance issues.',
          'IT Bot Alerts — device and software compliance.',
          'Infosec Bot Alerts — security and policy violations.',
        ],
      },
      { t: 'p', text: 'Each bot tab carries a red badge showing how many of its alerts are still pending.' },
      { t: 'h', text: 'The three bots' },
      {
        t: 'cards',
        items: [
          ['UserCog', 'HR Bot', 'Employee engagement monitoring — late starts, early finishes, short days, long absences and low activity.'],
          ['Laptop', 'IT Bot', 'Device and software compliance — Shoot install status, app version and prohibited application use.'],
          ['ShieldAlert', 'Infosec Bot', 'Security and policy monitoring — facial-recognition failures and work outside the approved location.'],
        ],
      },
      { t: 'callout', tone: 'info', title: 'The IT bot is not raising alerts yet', text: 'Its checks are defined but not yet wired to the tracker, so the IT Bot Alerts tab — and the Critical Alerts tab — stay empty for now. Everything the HR and Infosec bots raise is live.' },
      { t: 'h', text: 'What the bots watch for' },
      {
        t: 'table',
        head: ['Alert', 'Bot', 'Raised when'],
        rows: [
          ['Missed Work Start Time', 'HR', 'First activity of the day is more than 30 minutes after the scheduled start.'],
          ['Missed Work End Time', 'HR', 'Last activity of the day is more than 30 minutes before the scheduled end.'],
          ['Insufficient Work Hours', 'HR', 'Hours worked, including approved PTO, come to less than 7 for the day.'],
          ['Low Activity Level', 'HR', 'Average activity for the day is 30% or lower.'],
          ['Unscheduled Absence', 'HR', 'No activity for more than 90 minutes inside working hours, with no approved PTO.'],
          ['Location Violation', 'HR', 'A captured location is more than a mile from the member’s registered work location.'],
          ['Unrecognized Facial Recognition', 'Infosec', 'A webcam audit fails to match the member’s registered face three times in a day.'],
          ['Not Found Facial ID', 'Infosec', 'A webcam audit runs but the member has no registered face on file.'],
        ],
      },
      { t: 'h', text: 'When the bots run' },
      { t: 'p', text: 'The bots work on two different rhythms, and both skip weekends, holidays and days covered by approved PTO.' },
      {
        t: 'bullets',
        items: [
          'During the day — a sweep every 10 minutes catches unscheduled absences and late starts while there is still time to act. A member is only checked inside the working-hours window that applies that day — their secondary schedule on its chosen days, their primary schedule otherwise — and each type can only fire once per person per day.',
          'After the day closes — a schedule-adherence sweep reviews the full day once the member’s local clock passes 6am the next morning, and raises the start, end, hours and activity alerts together. Days with under 10 minutes of tracked time are skipped.',
          'Immediately — location violations and facial-recognition failures are raised the moment the offending activity or webcam audit is received.',
        ],
      },
      { t: 'h', text: 'Choosing who the bots monitor' },
      { t: 'p', text: 'Alert behaviour is set per member from Manage Members — open a member’s “···” menu and choose Setup Alerts. Four independent switches:' },
      {
        t: 'bullets',
        items: [
          'HR Bot Alert — when an HR alert is raised for this member, email the acknowledgement request automatically instead of waiting for a manager to send it.',
          'IT Bot Alert — the same automatic behaviour for IT alerts.',
          'InfoSec Bot Alert — the same automatic behaviour for security alerts.',
          'Schedule Adherence (HR Bot) — opt this member into the end-of-day sweep. Without it, they are never assessed for late starts, early finishes, short hours or low activity.',
        ],
      },
      { t: 'callout', tone: 'warn', title: 'Schedule adherence is opt-in', text: 'The four end-of-day HR checks only run for members whose Schedule Adherence box is ticked. Tick it for anyone whose working hours you actually want enforced — nothing is raised for them otherwise.' },
      { t: 'h', text: 'The life of an alert' },
      { t: 'p', text: 'An alert is closed by the employee acknowledging it, not by a manager dismissing it. There is no way to reject or delete an alert.' },
      {
        t: 'table',
        head: ['Status', 'What it means'],
        rows: [
          ['Pending', 'The bot raised it. Nobody has asked the employee to acknowledge it yet.'],
          ['AcknowledgementSent', 'The employee has been emailed a link and Shoot is waiting on their signature.'],
          ['Resolved', 'The employee signed and submitted. The alert is closed.'],
        ],
      },
      {
        t: 'steps',
        items: [
          ['Review the alert', 'Open the bot’s tab and click View Issue on the row. The panel shows who it is about, the description, the exact times behind it, and — for a location violation — a map of where the member actually was.'],
          ['Send the acknowledgement', 'Click Send Acknowledgement. The employee is emailed a link and the alert moves to AcknowledgementSent. If the member has the matching auto-send switch on, this happens by itself.'],
          ['Employee signs', 'They open the link, read the alert, sign in the box and submit.'],
          ['Alert resolves', 'The signature is saved as a PDF record and the alert moves to Resolved.'],
        ],
      },
      { t: 'callout', tone: 'warn', title: 'The acknowledgement link expires after 24 hours', text: 'If the employee misses the window the link stops working and the alert stays at AcknowledgementSent. Send a fresh acknowledgement from the View Issue panel to reissue it.' },
      { t: 'h', text: 'What the employee sees' },
      { t: 'p', text: 'The emailed link opens a single page headed ACTION REQUIRED. It shows their name and account, the alert description in plain language, and the times or locations behind it — with a toggle to read everything in UTC or their own timezone.' },
      { t: 'p', text: 'Below that is a signature box. They sign with a mouse or trackpad and click Acknowledge Alert; Shoot turns the whole page into a signed PDF and files it against the alert. Members can also see their own alerts and their status on the Recent Alerts card of their Account page.' },
      { t: 'h', text: 'Reading the alerts table' },
      {
        t: 'table',
        head: ['Column', 'Shows'],
        rows: [
          ['Employee Name', 'Who the alert is about; click through to their profile.'],
          ['Engagement Score', 'Their activity today, colour-coded — green at 70% and above, amber from 40%, red below.'],
          ['Hours Worked', 'Hours tracked today.'],
          ['Idle Time', 'Non-active hours today.'],
          ['Alerts Triggered', 'The condition the bot checked. Hover for the full text.'],
          ['Date', 'When the alert was raised. The only sortable column.'],
          ['Action', 'View Issue — opens the detail panel.'],
        ],
      },
      { t: 'callout', tone: 'warn', title: 'The middle three columns are about today', text: 'Engagement Score, Hours Worked and Idle Time always show the member’s figures for today, not for the day the alert was raised. On older alerts they will usually read 0% and 0h. Use View Engagement in the detail panel to see the day that actually matters.' },
      { t: 'p', text: 'The Filters button narrows the list by employee, by bot category and by status. Set your filters and click Search — Reset clears the selections but does not re-run the list until you search again.' },
      { t: 'h', text: 'Emails the bots send' },
      {
        t: 'bullets',
        items: [
          'Acknowledgement request — to the employee, whenever an acknowledgement is sent for one of their alerts.',
          'Schedule adherence alert — to the employee with their reporting manager copied, summarising every breach from that day and linking to the activity behind it. It names the schedule the day was judged against; only a workspace admin can change it.',
          'Manager digest — one email per manager per day, listing each team member flagged that day. HR is copied when a workspace HR address is configured, and receives the digest directly for anyone without a manager.',
        ],
      },
      { t: 'h', text: 'Who can do what' },
      {
        t: 'bullets',
        items: [
          'Sending acknowledgements and resolving alerts requires Workspace admin or above, or a Team Leader with team members.',
          'Configuring Setup Alerts for a member requires Workspace admin or above.',
          'Members can always see their own alerts on their Account page.',
        ],
      },
      { t: 'callout', tone: 'tip', title: 'Fix the schedule, not just the alert', text: 'Repeated Missed Work Start Time alerts for the same person usually mean their configured working hours no longer match reality. Update their working hours in Settings › Working Hours — or give them a secondary schedule for the days they work different hours — rather than acknowledging the same alert every morning.' },
    ],
  },
  // ───────────────────────────────────── MCP connector
  {
    id: 'mcp-connector',
    label: 'MCP connector',
    icon: 'Plug',
    title: 'Connecting an AI client (MCP)',
    lead: 'The MCP connector lets you ask questions about your workspace’s activity data from an AI client such as Claude or ChatGPT, instead of reading it off a screen. It is read-only, it never reaches outside the workspaces you already administer, and by default only owners can use it.',
    where: 'Settings & Permissions → MCP Connector',
    blocks: [
      { t: 'h', text: 'How to connect' },
      { t: 'p', text: 'Paste one command into your terminal. You will be asked to sign in with your Shoot email and password, or Google SSO, the first time it connects — there is no API key to create or store.' },
      {
        t: 'code',
        label: 'Production — the live data behind your Reports screens',
        lines: ['claude mcp add --transport http shoot-analytics https://api.get-shoot.com/api/mcp --scope user'],
      },
      {
        t: 'code',
        label: 'Sign in to the connector',
        lines: ['claude mcp login shoot-analytics'],
      },
      { t: 'p', text: 'Confirm it was added and is reachable:' },
      {
        t: 'code',
        label: 'List your connectors',
        lines: ['claude mcp list'],
      },
      { t: 'p', text: 'To remove it again:' },
      {
        t: 'code',
        label: 'Remove the production connector',
        lines: ['claude mcp remove shoot-analytics --scope user'],
      },
      { t: 'p', text: 'For Claude Desktop there is no command — paste this into your claude_desktop_config.json instead:' },
      {
        t: 'code',
        label: 'Claude Desktop configuration',
        lines: [
          '{',
          '  "mcpServers": {',
          '    "shoot-analytics": {',
          '      "type": "http",',
          '      "url": "https://api.get-shoot.com/api/mcp"',
          '    }',
          '  }',
          '}',
        ],
      },
      { t: 'callout', tone: 'warn', title: 'Keep the two environments apart', text: 'Production and staging look identical once connected. Add the production one as “shoot-analytics” and staging as “shoot-analytics-staging”, exactly as above, so an unqualified question cannot land on the wrong data.' },
      { t: 'callout', tone: 'info', title: 'If it refuses, or a workspace is missing', text: 'The connector only returns workspaces you are allowed to connect to, and that is set per workspace. Open Settings → MCP Connector: if you cannot see that tab you are not an owner, and if a workspace is missing from your answers its owner has not allowed your account type there.' },
      { t: 'callout', tone: 'tip', title: 'Check the numbers once', text: 'Ask something you can verify against a screen — “hours worked by department last month” — and compare it with Reports. If it does not match, you are probably connected to the wrong environment.' },
      { t: 'h', text: 'What it is' },
      { t: 'p', text: 'MCP — Model Context Protocol — is an open standard that lets an AI client read data from a system on your behalf. Pointing a client at Shoot’s connector gives it a set of read-only analytics tools, so you can ask for a department breakdown or a week-over-week trend in plain language and get numbers that match the Reports screens.' },
      {
        t: 'cards',
        items: [
          ['Table', 'Read-only', 'The connector can only run pre-built SELECT queries. Nothing in Shoot can be edited, deleted or configured through it.'],
          ['KeyRound', 'Your access, not more', 'You reach exactly the workspaces your own membership entitles you to. Someone who cannot see a workspace in Shoot cannot see it here either.'],
          ['ShieldAlert', 'Fully audited', 'Every question is written to the activity log with your name, the tool used and the rows returned — the same trail the rest of the product uses.'],
        ],
      },
      { t: 'h', text: 'Who is allowed to connect' },
      { t: 'p', text: 'Out of the box only the Primary owner and Workspace owners can connect. That is deliberately narrower than the Reports screens: a connected client can read the whole workspace’s activity in one go, so the default is the smallest group that already has that reach.' },
      { t: 'p', text: 'A workspace owner can widen it in two independent ways, and a member only needs one of them:' },
      {
        t: 'table',
        head: ['Way in', 'Where you set it', 'Use it when'],
        rows: [
          ['Account type', 'Settings → MCP Connector → Account types allowed to connect', 'A whole tier should have the connector — for example every Workspace admin.'],
          ['Individual assignment', 'Settings → MCP Connector → Assign individual members', 'One person needs it without being promoted — an analyst on a Team Leader account, say.'],
        ],
      },
      { t: 'callout', tone: 'info', title: 'Assigning someone never removes anyone', text: 'The two lists add together. Assigning a Team Leader individually does not affect the owners who are allowed by account type — to take access away, untick the account type instead.' },
      { t: 'p', text: 'Members on a custom role are compared by role level against the lowest account type you have ticked, because a custom role has no account type of its own. The member table on that screen shows what each person’s current position is before you change anything.' },
      { t: 'h', text: 'What you can ask it' },
      { t: 'p', text: 'The connector exposes the daily activity rollups, not raw interval data. That shapes what it can answer:' },
      {
        t: 'bullets',
        items: [
          'Tracked, active, idle and manual hours per member, team or department, over any date range.',
          'Activity percentage and the High / Average / Low performance bands, using the same thresholds as the Team Performance screen.',
          'Trends by day, week or month, for spotting when output changed.',
          'Attendance, time off, unusual-activity events and open alerts.',
          'Times of day and shift patterns — but only within the current month, because interval data is pruned after that.',
        ],
      },
      { t: 'callout', tone: 'warn', title: 'It cannot see screenshots or applications', text: 'Screenshots, webcam captures, keylogger text, browser history, per-application breakdowns and location coordinates are all excluded from the connector. Ask for those on the screens that are built to show them, where the usual access rules and audit apply.' },
      { t: 'h', text: 'Turning it off' },
      { t: 'p', text: 'The switch at the top of Settings → MCP Connector disables the connector for the whole workspace, individually-assigned members included. Existing clients stop getting answers immediately — there is no session to expire and nothing cached to clear.' },
      { t: 'h', text: 'Who can do what' },
      {
        t: 'bullets',
        items: [
          'Changing who may connect requires Workspace owner or Primary owner — Workspace admins cannot widen their own access.',
          'Connecting requires an allowed account type, or an individual assignment, in at least one workspace.',
          'Every change to these settings is written to the activity log, with the previous and new values.',
        ],
      },
    ],
  },
];

export const ACTIVITY_TYPES = [
  { letter: 'D', color: '#213D69', name: 'Deep Focused', desc: 'Sustained, high-concentration work. The strongest signal of quality output.', scored: true },
  { letter: 'F', color: '#5A81BC', name: 'Focused', desc: 'Productive work with normal interaction. Counts toward quality time.', scored: true },
  { letter: 'A', color: '#B5BAC4', name: 'Active', desc: 'General active use of the computer. Lighter, but still engaged.', scored: true },
  { letter: 'W', color: '#F19235', name: 'Work Break', desc: 'Logged breaks. Tracked for context but excluded from the score.', scored: false },
  { letter: 'M', color: '#265EB8', name: 'Manual Hours', desc: 'Time added manually rather than auto-tracked. Reported separately.', scored: false },
];

export default ARTICLES;
